import { useMutation, useQuery } from "@apollo/client"
import { Dialog, Switch } from "@headlessui/react"
import cn from "classnames"
import { motion, AnimatePresence } from "framer-motion"
import Cookies from "js-cookie"
import React, { useCallback, useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { getFragment, graphql } from "@/api"
import { type CurrentCookieConsentQuery } from "@/api/graphql"
import { pushConsentToDataLayer, isTablet, transitionTime } from "@/common/util"
import { ChevronDownLarge, CheckmarkLarge, CheckmarkSmall, CloseSmall, CloseLarge } from "@/skylticons"

export const CookieConsentFragment = graphql(`
  fragment CookieCheckbox_CookieCheckbox on CookieConsent {
    id

    optin {
      key
      value
      __typename
    }

    __typename
  }
`)

export const currentCookieConsentDoc = graphql(`
  query CurrentCookieConsent {
    currentCookieConsent {
      ...CookieCheckbox_CookieCheckbox
    }
  }
`)

const cookieConsentUpdateDoc = graphql(`
  mutation CookieConsentUpdate(
    $id: ID!
    $functional: Boolean!
    $analytics: Boolean!
    $marketing: Boolean!
    $cookieText: String
    $buttonText: String
  ) {
    cookieConsentUpdate(
      id: $id
      functional: $functional
      analytics: $analytics
      marketing: $marketing
      cookieText: $cookieText
      buttonText: $buttonText
    ) {
      consent {
        ...CookieCheckbox_CookieCheckbox
      }
    }
  }
`)

export const cookieConsentCreateDoc = graphql(`
  mutation CookieConsentCreate(
    $functional: Boolean!
    $analytics: Boolean!
    $marketing: Boolean!
    $cookieText: String
    $buttonText: String
  ) {
    cookieConsentCreate(
      functional: $functional
      analytics: $analytics
      marketing: $marketing
      cookieText: $cookieText
      buttonText: $buttonText
    ) {
      consent {
        ...CookieCheckbox_CookieCheckbox
      }
    }
  }
`)

interface AccordionProps {
  title: string
  itemKey: string
  content: string
  className?: string
  alwaysActive?: boolean
  optedIn?: boolean
  toggleSelected?: (key: string, value: boolean) => void
  textRef?: React.Ref<HTMLDivElement>
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  itemKey,
  content,
  className,
  alwaysActive,
  optedIn,
  toggleSelected,
  textRef,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(false)

  const toggleOpen = useCallback((e: React.MouseEvent) => {
    if (e.target instanceof Element && !e.target.closest(".toggle-button")) {
      setOpen(v => !v)
    }
  }, [])

  useEffect(() => {
    setSelected(!!optedIn)
  }, [optedIn])

  return (
    <div className={cn("accordion border-t border-white-20", className, itemKey)}>
      <div className="flex items-center">
        <button
          id={`accordion-button-${itemKey}`}
          className="flex w-full cursor-pointer items-center justify-between"
          onClick={e => toggleOpen(e)}
          aria-expanded={open}
          aria-controls={`panel-${itemKey}`}
          type="button"
        >
          <div className="my-3 ml-4 flex w-full">
            <ChevronDownLarge
              className={cn("mr-3 h-6 w-6 transition duration-150 ease-in-out", { "-scale-y-100": open })}
            />
            <div className="text-sm font-normal lg:text-base" ref={textRef}>
              {title}
            </div>
          </div>
          <div className="my-3 mr-4 whitespace-nowrap">
            {alwaysActive && (
              <>
                <CheckmarkLarge className="mr-1 h-6 w-6 text-primary" />
                <span className="text-xs font-semibold leading-[18px] text-primary">{t("cookie_settings.active")}</span>
              </>
            )}
          </div>
        </button>
        {toggleSelected && (
          <Switch
            checked={selected}
            onChange={() => toggleSelected(itemKey, !selected)}
            onClick={e => e.stopPropagation()}
            className={cn(
              "toggle-button relative mr-4 flex h-6 w-12 cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out",
              {
                "enabled bg-primary onlyHover:hover:bg-blue-600": selected,
                "disabled onlyHover:hover:bg-dark bg-ink-50": !selected,
              }
            )}
          >
            <span className="sr-only">{title}</span>
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out",
                { "translate-x-6": selected, "translate-x-0": !selected }
              )}
            />
            <CheckmarkSmall
              className={cn("relative left-0 h-6 w-6 text-white", {
                "opacity-100": selected,
                "opacity-0": !selected,
              })}
            />
            <CloseSmall
              className={cn("relative left-0 h-6 w-6 text-white", {
                "opacity-0": selected,
                "opacity-100": !selected,
              })}
            />
          </Switch>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id={`panel-${itemKey}`}
            className="overflow-hidden"
            role="region"
            aria-labelledby={`accordion-button-${itemKey}`}
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: transitionTime(0.15), ease: "easeInOut" }}
            aria-hidden={!open}
          >
            <div className="mx-4 pb-8 pt-2 text-sm font-normal" dangerouslySetInnerHTML={{ __html: content }}></div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

interface CookieChoicesProps {
  privacyURL: string
  wrapper: boolean
  show?: boolean
  handleClose?: () => void
  updateCookie?: (
    functional: boolean,
    analytics: boolean,
    marketing: boolean,
    cookieText: string,
    buttonText: string
  ) => void
}

const CookieChoices: React.FC<CookieChoicesProps> = ({ privacyURL, wrapper, show, handleClose, updateCookie }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [functionalSelected, setFunctionalSelected] = useState(false)
  const [analyticsSelected, setAnalyticsSelected] = useState(false)
  const [marketingSelected, setMarketingSelected] = useState(false)

  const cookieResult = useQuery(currentCookieConsentDoc)
  const currentCookieFragment = cookieResult.data?.currentCookieConsent
  const currentCookie = getFragment(CookieConsentFragment, currentCookieFragment)

  const toggleModal = useCallback(() => {
    if (handleClose) {
      handleClose()
    } else {
      setOpen(v => !v)
    }
  }, [handleClose])

  const overlayClick = useCallback((e: MouseEvent) => {
    if (e.target instanceof Element) {
      if (!e.target.closest(".cookie-settings-content")) {
        setOpen(false)
      }
    }
  }, [])

  useEffect(() => {
    setOpen(!!show)
  }, [show])

  useEffect(() => {
    document.addEventListener("click", overlayClick, { capture: true })

    return () => document.removeEventListener("click", overlayClick, { capture: true })
  }, [overlayClick])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (wrapper && e.key === "Escape" && open) {
        setOpen(v => !v)
      }
    }

    document.addEventListener("keydown", handleKeyDown, { capture: true })

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true })
    }
  }, [wrapper, open])

  useEffect(() => {
    setFunctionalSelected(currentCookie?.optin.find(item => item.key === "cookiesEnableFunctional")?.value == "true")
    setAnalyticsSelected(currentCookie?.optin.find(item => item.key === "cookiesEnableAnalytics")?.value == "true")
    setMarketingSelected(currentCookie?.optin.find(item => item.key === "cookiesEnableMarketing")?.value == "true")
  }, [currentCookie])

  const [cookieConsentCreateMutation, cookieConsentCreateMutationResult] = useMutation(cookieConsentCreateDoc)
  const [cookieConsentUpdateMutation, cookieConsentUpdateMutationResult] = useMutation(cookieConsentUpdateDoc)

  const saveConsent = useCallback(
    async (functional: boolean, analytics: boolean, marketing: boolean, cookieText: string, buttonText: string) => {
      let consent
      if (currentCookie) {
        const result = await cookieConsentUpdateMutation({
          variables: {
            id: currentCookie.id,
            functional: functional,
            analytics: analytics,
            marketing: marketing,
            cookieText: cookieText,
            buttonText: buttonText,
          },
          update: (client, { data }) => {
            if (data?.cookieConsentUpdate?.consent) {
              client.writeQuery<CurrentCookieConsentQuery>({
                query: currentCookieConsentDoc,
                data: { currentCookieConsent: data.cookieConsentUpdate.consent },
              })
            }
          },
        })

        consent = getFragment(CookieConsentFragment, result.data?.cookieConsentUpdate?.consent)

        pushConsentToDataLayer(functional, analytics, marketing)
      } else {
        const result = await cookieConsentCreateMutation({
          variables: {
            functional: functional,
            analytics: analytics,
            marketing: marketing,
            cookieText: cookieText,
            buttonText: buttonText,
          },
          update: (client, { data }) => {
            if (data?.cookieConsentCreate?.consent) {
              client.writeQuery<CurrentCookieConsentQuery>({
                query: currentCookieConsentDoc,
                data: { currentCookieConsent: data.cookieConsentCreate.consent },
              })
            }
          },
        })

        consent = getFragment(CookieConsentFragment, result.data?.cookieConsentCreate?.consent)

        pushConsentToDataLayer(functional, analytics, marketing)
      }

      if (consent?.id) {
        Cookies.set("cookies_consented", consent.id, { expires: 180 }) // 6 mo
      }
    },
    [currentCookie, cookieConsentCreateMutation, cookieConsentUpdateMutation]
  )

  const saveSettings = useCallback(
    (buttonText: string, cookieText: string) => {
      if (updateCookie) {
        updateCookie(functionalSelected, analyticsSelected, marketingSelected, buttonText, cookieText)
      } else {
        saveConsent(functionalSelected, analyticsSelected, marketingSelected, buttonText, cookieText)
        setOpen(v => !v)
      }
    },
    [saveConsent, functionalSelected, analyticsSelected, marketingSelected, updateCookie]
  )

  const toggleCookieSelected = useCallback((key: string, value: boolean) => {
    if (key == "functional") {
      setFunctionalSelected(value)
    } else if (key == "analytics") {
      setAnalyticsSelected(value)
    } else if (key == "marketing") {
      setMarketingSelected(value)
    }
  }, [])

  const loading = cookieConsentCreateMutationResult.loading || cookieConsentUpdateMutationResult.loading
  const descriptionRef = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLDivElement>(null)
  const dataRef = useRef<HTMLDivElement>(null)
  const marketingRef = useRef<HTMLDivElement>(null)

  const content = (
    <Dialog.Panel className="cookie-settings-content visible-scrollbar z-50 h-full max-h-full max-w-[600px] sm:h-3/4 lg:fixed lg:left-0 lg:top-0 lg:h-full lg:w-[480px]">
      <div className="relative flex h-full flex-col overflow-y-scroll rounded-t-2xl bg-white pl-3 pr-3 pt-8 shadow-xl sm:rounded-2xl lg:h-full lg:rounded-l-none lg:rounded-r-2xl lg:pl-6 lg:pr-4">
        <a
          className="close button button-large button-subtle icon-only absolute right-2 top-2 mb-0 lg:right-4 lg:top-4"
          onClick={toggleModal}
        >
          <CloseLarge />
        </a>
        <Dialog.Title as="h4">{t("cookie_settings.heading")}</Dialog.Title>
        <Dialog.Description as="div">
          <>
            <div className="mb-2 text-sm font-normal" ref={descriptionRef}>
              {t("cookie_settings.description", { domain: t("site.domain") })}
            </div>
            <div className="mb-6 lg:mb-8">
              <a href={`${privacyURL}#cookie-paragraph`} className="link-subtle underline" onClick={toggleModal}>
                {t("cookie_settings.more_info")}
              </a>
            </div>
            <h4 className="heading-four !mb-4 lg:!mb-6">{t("cookie_settings.manage_heading")}</h4>

            <Accordion
              title={t("cookie_settings.necessary_heading")}
              alwaysActive
              itemKey="necessary"
              content={t("cookie_settings.necessary_description")}
            />

            <Accordion
              title={t("cookie_settings.chat_heading")}
              optedIn={functionalSelected}
              itemKey="functional"
              content={t("cookie_settings.chat_description")}
              toggleSelected={toggleCookieSelected}
              textRef={chatRef}
            />

            <Accordion
              title={t("cookie_settings.analytics_heading")}
              optedIn={analyticsSelected}
              itemKey="analytics"
              content={t("cookie_settings.analytics_description")}
              toggleSelected={toggleCookieSelected}
              textRef={dataRef}
            />

            <Accordion
              title={t("cookie_settings.marketing_heading")}
              className="mb-9 border-b"
              optedIn={marketingSelected}
              itemKey="marketing"
              content={t("cookie_settings.marketing_description")}
              toggleSelected={toggleCookieSelected}
              textRef={marketingRef}
            />
          </>
        </Dialog.Description>

        <div className="sticky bottom-0">
          <div className={cn("pointer-events-none absolute -mt-10 min-h-12 w-full bg-gradient-to-t from-white")}></div>
          <div className="modal-actions flex flex-col-reverse bg-white pb-5 pt-2 sm:flex-row">
            <button
              id="save-cookie-settings"
              className="button button-secondary button-large !mb-0 w-full sm:flex-1"
              onClick={e => {
                if (descriptionRef.current && chatRef.current && dataRef.current && marketingRef.current) {
                  const text =
                    e.currentTarget.innerHTML +
                    (functionalSelected && chatRef.current.innerHTML ? `, ${chatRef.current.innerHTML}` : "") +
                    (analyticsSelected && dataRef.current.innerHTML ? `, ${dataRef.current.innerHTML}` : "") +
                    (marketingSelected && marketingRef.current?.innerHTML ? `, ${marketingRef.current.innerHTML}` : "")

                  saveSettings(descriptionRef.current.innerHTML, text)
                }
              }}
              disabled={loading}
              type="button"
            >
              {t("cookie_settings.save")}
            </button>
            <button
              id="accept-cookies-choices"
              className="button button-primary button-large mb-2 ml-0 w-full sm:!mb-0 sm:!ml-2 sm:flex-1"
              onClick={e => {
                if (updateCookie && descriptionRef.current) {
                  updateCookie(true, true, true, descriptionRef.current.innerText, e.currentTarget.innerText)
                } else if (descriptionRef.current) {
                  saveConsent(true, true, true, descriptionRef.current.innerText, e.currentTarget.innerText)
                  setOpen(v => !v)
                }
              }}
              type="button"
            >
              {t("cookies.accept_all")}
            </button>
          </div>
        </div>
      </div>
    </Dialog.Panel>
  )

  if (!wrapper) {
    return content
  }

  return (
    <>
      <button id="cookie-settings" className="button button-primary !mb-0" onClick={toggleModal} type="button">
        {t("cookie_settings.heading")}
      </button>

      <>
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-black bg-opacity-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: transitionTime(0.3) }}
              />

              <Dialog open={open} as="div" className="cookie-settings-modal relative z-50" onClose={() => toggleModal}>
                <div className="overlay fixed inset-0 z-50">
                  {isTablet() ? (
                    <motion.div
                      className="flex h-full w-full items-center justify-center"
                      initial={{ translateY: "100vh" }}
                      animate={{ translateY: 0 }}
                      exit={{ translateY: "100vh" }}
                      transition={{ duration: transitionTime(0.3), ease: "easeInOut" }}
                    >
                      {content}
                    </motion.div>
                  ) : (
                    <motion.div
                      className="flex h-full w-full items-center justify-center"
                      initial={{ translateX: -480 }}
                      animate={{ translateX: 0 }}
                      exit={{ translateX: -480 }}
                      transition={{ duration: transitionTime(0.3), ease: "easeInOut" }}
                    >
                      {content}
                    </motion.div>
                  )}
                </div>
              </Dialog>
            </>
          )}
        </AnimatePresence>
      </>
    </>
  )
}

export default CookieChoices
